// origin: WS_DeskOS artifacts/edge-tracker/src/i18n/core.ts (one shared copy instead of a web and a server copy;
// EN is the source language of Prop_DeskOS, RU and ES are translations).
/**
 * i18n core: languages, dictionary types, interpolation and plurals. Pure (no React, no DOM, no state), so it
 * runs in tests, workers, the API and the SPA.
 *
 * Dictionary shape (EN is the source of truth, `as const` so tsc sees literal texts and their {params}):
 *   export default {
 *     save: "Save",
 *     greeting: "Hello, {name}",
 *     trades: { one: "{count} trade", other: "{count} trades" },
 *     filters: { side: { long: "Long", short: "Short" } },        // nesting → key "filters.side.long"
 *   } as const;
 * RU/ES files: `export default { … } satisfies Messages<typeof en>;` → tsc rejects missing or extra keys.
 * A plural entry is an object with `one` + `other` (RU adds `few` + `many`); never use `one`/`other` as names of
 * ordinary nested keys.
 */

export type Lang = "en" | "ru" | "es";
export const LANGS: readonly Lang[] = ["en", "ru", "es"];
export const DEFAULT_LANG: Lang = "en";

/** Native names for the switcher (never translated; escaped so code stays free of Cyrillic) and BCP 47 tags. */
export const LANG_META: Record<Lang, { native: string; code: string; tag: string }> = {
  en: { native: "English", code: "EN", tag: "en-US" },
  ru: { native: "Русский", code: "RU", tag: "ru-RU" },
  es: { native: "Español", code: "ES", tag: "es-ES" },
};

export function isLang(v: unknown): v is Lang {
  return v === "en" || v === "ru" || v === "es";
}

/** "en", "EN", "en-US", "es-419", "ru,en;q=0.8" → Lang; anything else → null. */
export function parseLang(v: unknown): Lang | null {
  if (typeof v !== "string") return null;
  const base = v.trim().toLowerCase().split(/[-_,;]/)[0];
  return isLang(base) ? base : null;
}

/**
 * Locale for Intl.NumberFormat. Spanish digits take the platform's comma shape, the same as ru: a decimal comma and a
 * non-breaking-space thousands group from four digits on ("1 199,50", ADR-0065). CLDR es-ES groups with a point and
 * not below five digits ("1199" next to "12.345"), which reads badly in a column of prices, and Latin American
 * locales differ among themselves (es-MX "1,199.50"); RAE accepts the space group.
 */
export function numberLocaleFor(lang: Lang): string {
  return lang === "es" ? LANG_META.ru.tag : LANG_META[lang].tag;
}

/** BCP 47 tag for Intl (dates, <html lang>): en-US / ru-RU / es-ES. */
export function localeTagFor(lang: Lang): string {
  return LANG_META[lang].tag;
}

// ─── Types ───────────────────────────────────────────────────────────────────────────────────────────────

export type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";
/** RU: one/few/many/other. EN, ES: one/other (ES may add many for millions; a missing form falls back to other). */
export type PluralForms = { one: string; other: string; few?: string; many?: string };
type PluralLike = { readonly one: string; readonly other: string };

export type Dict = { readonly [key: string]: string | PluralLike | Dict };

/** Shape RU/UZ must satisfy for an EN dictionary T: same keys, any text, plural entries may add few/many. */
export type Messages<T> = {
  [K in keyof T]: T[K] extends string ? string : T[K] extends PluralLike ? PluralForms : Messages<T[K]>;
};

type Join<P extends string, K extends string> = P extends "" ? K : `${P}.${K}`;

/** Every translatable key of T as a dotted path ("filters.side.long"). */
export type Keys<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? Join<P, K> : T[K] extends PluralLike ? Join<P, K> : Keys<T[K], Join<P, K>>;
}[keyof T & string];

/** Entry at a dotted path. */
export type ValueAt<T, K extends string> = K extends `${infer H}.${infer R}`
  ? H extends keyof T
    ? ValueAt<T[H], R>
    : never
  : K extends keyof T
    ? T[K]
    : never;

/** "{name} and {count}" → "name" | "count" (works on `as const` literals; plain `string` yields never). */
export type ExtractParams<S> = S extends `${string}{${infer P}}${infer Rest}` ? P | ExtractParams<Rest> : never;

type ParamNames<V> = V extends string ? ExtractParams<V> : V extends PluralLike ? ExtractParams<V[keyof V] & string> : never;

export type ParamValue = string | number;
export type Params = Record<string, ParamValue>;

/** Arguments after the key: plural → `{ count, …params }` required; text with {params} → those params required; else optional. */
export type TArgs<V> = V extends PluralLike
  ? [params: { count: number } & { [P in Exclude<ParamNames<V>, "count">]: ParamValue }]
  : [ParamNames<V>] extends [never]
    ? [params?: Params]
    : [params: { [P in ParamNames<V>]: ParamValue }];

/** Typed translator for dictionary D. */
export type TFunction<D> = <K extends Keys<D>>(key: K, ...args: TArgs<ValueAt<D, K>>) => string;

// ─── Runtime ─────────────────────────────────────────────────────────────────────────────────────────────

const pluralRulesCache = new Map<Lang, Intl.PluralRules>();
const numberFmtCache = new Map<Lang, Intl.NumberFormat>();

/** CLDR plural category for `n` in `lang` (ru: one/few/many/other; en: one/other; es: one/many/other). */
export function pluralCategory(lang: Lang, n: number): PluralCategory {
  let pr = pluralRulesCache.get(lang);
  if (!pr) {
    pr = new Intl.PluralRules(lang);
    pluralRulesCache.set(lang, pr);
  }
  return pr.select(n) as PluralCategory;
}

/** Locale number grouping for counts (1,234 / 1 234). */
export function formatCountFor(lang: Lang, n: number): string {
  let nf = numberFmtCache.get(lang);
  if (!nf) {
    nf = new Intl.NumberFormat(numberLocaleFor(lang), { maximumFractionDigits: 2 });
    numberFmtCache.set(lang, nf);
  }
  return nf.format(n);
}

/** Pick the form for `count`; a missing category falls back to other → many → one. */
export function selectPlural(lang: Lang, forms: PluralForms, count: number): string {
  const cat = pluralCategory(lang, count);
  return (forms as Record<string, string | undefined>)[cat] ?? forms.other ?? forms.many ?? forms.one;
}

/**
 * "{name} opened {count}" + params → text. Unknown placeholders stay as written (visible in QA).
 * `count` is formatted with locale grouping; other values are inserted as is (pass preformatted strings for money).
 */
export function interpolate(template: string, params: Params | undefined, lang: Lang = DEFAULT_LANG): string {
  if (!params || template.indexOf("{") < 0) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = params[name];
    if (v === undefined || v === null) return whole;
    if (name === "count" && typeof v === "number") return formatCountFor(lang, v);
    return String(v);
  });
}

function isPluralEntry(v: unknown): v is PluralForms {
  return !!v && typeof v === "object" && typeof (v as PluralForms).one === "string" && typeof (v as PluralForms).other === "string";
}

/** Raw entry at a dotted path, or undefined. */
export function lookup(dict: Dict | undefined, key: string): string | PluralForms | undefined {
  if (!dict) return undefined;
  let cur: unknown = dict;
  for (const part of key.split(".")) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  if (typeof cur === "string" || isPluralEntry(cur)) return cur;
  return undefined;
}

/** Render one entry (string or plural) for `lang`. */
export function renderEntry(entry: string | PluralForms, lang: Lang, params?: Params): string {
  if (typeof entry === "string") return interpolate(entry, params, lang);
  const count = typeof params?.count === "number" ? params.count : Number(params?.count ?? 0);
  return interpolate(selectPlural(lang, entry, Number.isFinite(count) ? count : 0), params, lang);
}

/**
 * Translate `key`: the `lang` dictionary first, then the EN source (also used when a lazy dictionary failed to load).
 * A key missing everywhere returns the key itself (visible, never a crash).
 */
export function translate(dicts: { source: Dict; current?: Dict }, lang: Lang, key: string, params?: Params): string {
  const own = lang === DEFAULT_LANG ? undefined : lookup(dicts.current, key);
  if (own !== undefined) return renderEntry(own, lang, params);
  const base = lookup(dicts.source, key);
  if (base !== undefined) return renderEntry(base, DEFAULT_LANG, params);
  return key;
}
